import React from 'react';
import { BrowserSupport } from '@tableau/browser-support';
import { NavStyles } from './NavigationStyles';


export interface NavListItemState {
  readonly focus: boolean;
  readonly selected: boolean;
}

export type NavListItemRenderProp = (navListItemState: NavListItemState) => JSX.Element;

export interface NavListItem {
  readonly value: string;
  readonly content: NavListItemRenderProp;
  readonly disabled?: boolean;
}

export interface NavListProps {
  readonly items: NavListItem[];
  readonly selectedValue: string;
  readonly onSelect: (value: string) => void;
}

export interface NavListState {
  readonly activeItemIndex: number;
  readonly hasFocus: boolean;
  readonly showFocus: boolean;
}

export class NavList extends React.PureComponent<NavListProps, NavListState> {
  private listElement: HTMLElement = null;

  public constructor(props: NavListProps) {
    super(props);
    this.state = {activeItemIndex: this.selectedValueIndex(), hasFocus: false, showFocus: false};
  }

  public render() {
    /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    return (
      <ul
        tabIndex={0}
        ref={this.handleListRef}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
        onClick={this.handleClick}
        className={NavStyles.NavList}
      >
        {this.items()}
      </ul>
    );
  }

  private items = (): JSX.Element[] => {
    return this.props.items.map((item, index) => {
      const focus = index === this.state.activeItemIndex && this.state.hasFocus && this.state.showFocus;
      const selected = item.value === this.props.selectedValue;
      return (
        <li
          data-itemvalue={item.value}
          data-itemindex={index}
          key={index}
          tabIndex={-1}
          style={{outline: 'none'}}
        >
          {item.content({focus, selected})}
        </li>
      );
    });
  }

  private handleListRef = (ref: HTMLElement | null): void => {
    this.listElement = ref;
  }

  private selectedValueIndex = (): number => {
    return this.props.items.findIndex((item) => {
      return item.value === this.props.selectedValue;
    });
  }

  private handleFocus = (e: React.FocusEvent<HTMLElement>): void => {
    // we show focus when the previous focused item (relatedTarget) isn't part of NavList
    // and the NavList doesn't contain the current active element. We have to add an equality check
    // with the navlist itself since the contains function is inclusive.
    let showFocus = !this.listElement.contains(e.relatedTarget as any);
    if(!this.listElement.isEqualNode(document.activeElement)) {
      showFocus = showFocus && !this.listElement.contains(document.activeElement);
    }

    if (!this.state.hasFocus) {
      this.setState({ hasFocus: true, showFocus});
    }
  }

  private handleBlur = (e: React.FocusEvent<HTMLElement>): void => {
    // IE fires off blur events when focus changes for child elements
    // so don't reset activeItemIndex otherwise key navigation won't work.
    if (BrowserSupport.IsIE()) {
      this.setState({ hasFocus: false, showFocus: false });
    } else {
      // resetting back to active item index means the selected value will have focus
      // if user tabs back to left nav
      this.setState({ hasFocus: false, showFocus: false, activeItemIndex: this.selectedValueIndex() });
    }
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    const { items } = this.props;
    let { activeItemIndex } = this.state;
    switch (e.key) {
      case 'ArrowUp':
        activeItemIndex = activeItemIndex === 0 ? 0 : this.findNextActiveItem(activeItemIndex, -1);
        break;
      case 'ArrowDown':
        activeItemIndex = activeItemIndex === items.length - 1 ? activeItemIndex : this.findNextActiveItem(activeItemIndex, 1);
        break;
      case 'Enter':
        const activeItem: HTMLElement = this.listElement.querySelector(`[data-itemindex="${this.state.activeItemIndex}"]`);
        if (activeItem) {
          // Yes this is ugly code that requires knowing
          // there is an anchor tag inside each item.
          // Still working on cleaner solution
          const aTag = activeItem.getElementsByTagName('a').item(0);
          if (aTag) {
            aTag.click();
            const value = activeItem.getAttribute('data-itemvalue');
            this.props.onSelect(value);

            // Prevent the actual active <a> element from firing on enter keypress
            e.stopPropagation();
            e.preventDefault();
          }
        }
        return;
      default:
        return;
    }
    e.stopPropagation();
    e.preventDefault();
    if (activeItemIndex !== this.state.activeItemIndex) {
      this.setState({ activeItemIndex, showFocus: true });
    }
  }

  private findNextActiveItem(index: number, direction: number) {
    index += direction;
    while(this.props.items[index].disabled) index += direction;
    return index;
  }

  private handleClick = (e: React.SyntheticEvent<HTMLElement>): void => {
    const itemNode = this.closest(e.target as HTMLElement);
    if (!itemNode) { return; }
    const index = itemNode.getAttribute('data-itemindex');
    this.setState({activeItemIndex: Number(index)});

    const value = itemNode.getAttribute('data-itemvalue');
    this.props.onSelect(value);
  }

  //borrowed from widgets/DomUtil
  private closest(target: HTMLElement): HTMLElement {
    if (target.closest) return target.closest('li');

    // Fix for IE11 which doesnt have closest function
    const matches = Element.prototype.matches || Element.prototype.msMatchesSelector;
    if (!matches) { return null; }

    let node = target;
    while (node) {
      if (matches.call(node, 'li')) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }
}
